import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import moment, { Moment } from 'moment';
import { AttioQueryKeys } from 'common/constants/analyst-dashboards';
import { getValuationStatusOptions } from 'dashboard409a/services/attio-common';
import { getAttioValuationDetail } from 'dashboard409a/services/attio-operation';
import { SimplifiedAttioValuation } from 'dashboard409a/services/attio.types';
import { ModelWithValuationData } from 'dashboard409a/services/models.types';
import type { AttioValuationDetailResponse } from 'dashboard409a/services/shared-types';

/**
 * Calculates a date that is N business days from today.
 * Business days exclude weekends (Saturday and Sunday).
 * @param businessDays - Number of business days to add (default: 4)
 * @returns Date string in YYYY-MM-DD format
 */
export const addBusinessDays = (date?: Date | Moment | string, days: number = 4): string => {
  let result = moment(date || new Date());
  let daysAdded = 0;

  while (daysAdded < days) {
    result = result.add(1, 'days');

    if (result.day() !== 0 && result.day() !== 6) {
      daysAdded += 1;
    }
  }

  return result.format('YYYY-MM-DD');
};

/**
 * Type guard to check if data is already a full AttioValuationDetailResponse
 */
export const isFullDetailResponse = (value: unknown): value is AttioValuationDetailResponse =>
  value !== null &&
  typeof value === 'object' &&
  'valuedEntity' in value &&
  'valuedInformation' in value &&
  'deal' in value

/**
 * Helper to parse currency string to number
 */
const parseCurrency = (value: string | null | undefined): number => {
  if (!value) {
    return 0;
  }
  const num = Number(value.replaceAll(/[^0-9.-]/g, ''));
  return Number.isNaN(num) ? 0 : num;
};

/**
 * Transform SimplifiedAttioValuation to AttioValuationDetailResponse
 */
export const transformSimplifiedToDetail = (
  simplified: SimplifiedAttioValuation
): AttioValuationDetailResponse => ({
  valuedEntity: {
    valuedEntityId: simplified.record_id,
    companyName: simplified.company_name || '-',
    primaryContact: '-', // Not available in SimplifiedAttioValuation
    primaryContactId: undefined,
    category: '-', // Not available in SimplifiedAttioValuation
    jobTitle: '-', // Not available in SimplifiedAttioValuation
    salesRep: '-', // Not available in SimplifiedAttioValuation
    salesRepImageSrc: '', // Not available in SimplifiedAttioValuation
    companyUrl: undefined,
    valuedEntityNotice: undefined,
    primaryContactNotice: undefined,
  },
  valuedInformation: {
    valuationDate: simplified.valuation_date || '-',
    valuationName: simplified.company_name || '-',
    bookingType: '-', // Not available in SimplifiedAttioValuation
    bookingDate: simplified.created_at || '-',
    valuationLeadName: '-', // Not available in SimplifiedAttioValuation
    valuationLeadImageSrc: '-', // Not available in SimplifiedAttioValuation
    valuationId: simplified.record_id,
    renewalDate: '-', // Not available in SimplifiedAttioValuation
    valuationUrl: undefined,
    otherConsultantName: simplified.other_consultant || '-',
    otherConsultantImageSrc: simplified.other_consultant_image_src || '-',
    otherConsultantEmail: '-', // Not available in SimplifiedAttioValuation
    valuationNotice: undefined,
    valuationFee: parseCurrency(simplified.valuation_fee),
    valuationNetValue: simplified.net_value || '-',
    valuationEngagementManager: simplified.engagement_manager || '-',
    valuationEngagementManagerImageSrc: simplified.engagement_manager_image_src || '-',
    valuationEngagementManagerRole: '-', // Not available in SimplifiedAttioValuation
    // Financial fields
    split: parseCurrency(simplified.split),
    payment1: parseCurrency(simplified.payment_1),
    payment1Date: simplified.pay_1_date || undefined,
    payment2: parseCurrency(simplified.payment_2),
    payment2Date: simplified.pay_2_date || undefined,
    payment3: parseCurrency(simplified.payment_3),
    payment3Date: simplified.pay_3_date || undefined,
    refund: parseCurrency(simplified.refund),
    refundDate: simplified.refund_date || undefined,
    adjustment: parseCurrency(simplified.adjustment),
    adjustmentDate: simplified.adjustment_date || undefined,
    badDebt: parseCurrency(simplified.bad_debt),
    badDebtDate: simplified.bad_debt_date || undefined,
    renewalRate: parseCurrency(simplified.renewal_rate),
  },
  deal: {
    dealId: simplified.deal_id || simplified.record_id, // Fallback to record_id if deal_id not available
    valuationDate: simplified.valuation_date || '-',
    dealTotal: simplified.valuation_fee || '-',
    dealStage: simplified.deal_stage || '-',
    due: simplified.deal_due || simplified.due || '-',
    engagementManager: simplified.engagement_manager || '-',
    engagementManagerImageSrc: simplified.engagement_manager_image_src || '-',
    engagementManagerRole: '-', // Not available in SimplifiedAttioValuation
    dealUrl: undefined,
    dealName: 'Loading...',
    dealNotice: undefined,
    contractStatus: undefined,
    signedPDF: undefined,
    elURL: undefined,
    net: simplified.net_value || undefined,
    dealDate: simplified.valuation_date || undefined,
    dealStageOptions: undefined,
  },
  status: simplified.status || '-',
  statusId: undefined, // Will need to be fetched from status options if needed
});

interface UseValuationDetailProps {
  data: ModelWithValuationData | SimplifiedAttioValuation | string | undefined;
  open: boolean;
}

/**
 * Hook to handle valuation detail data transformation and API calls
 * Provides progressive/lazy loading: shows transformed data immediately, then enhances with API data
 */
export const useValuationDetail = ({ data, open }: UseValuationDetailProps) => {
  // Check if data already contains full detail (has valuedEntity, valuedInformation, and deal)
  const hasFullDetail = useMemo(() => isFullDetailResponse(data), [data]);

  // Extract valuationId from either ModelWithValuationData, SimplifiedAttioValuation, or string
  const valuationId = useMemo(() => {
    if (!data) {
      return undefined;
    }
    if (typeof data === 'string') {
      return data;
    }
    // Check if it's ModelWithValuationData (has attio_valuation_id) or SimplifiedAttioValuation (has record_id)
    return 'attio_valuation_id' in data ? data.attio_valuation_id : data.record_id;
  }, [data]);

  // Check if data is SimplifiedAttioValuation (from table row)
  const isSimplifiedValuation = useMemo(() => {
    if (!data || typeof data === 'string') {
      return false;
    }
    return 'record_id' in data && !('attio_valuation_id' in data) && !isFullDetailResponse(data);
  }, [data]);

  // Transform SimplifiedAttioValuation to AttioValuationDetailResponse if needed
  const transformedDetail = useMemo(() => {
    if (isSimplifiedValuation && data && typeof data !== 'string' && 'record_id' in data) {
      return transformSimplifiedToDetail(data as SimplifiedAttioValuation);
    }
    return undefined;
  }, [isSimplifiedValuation, data]);

  // Fetch status options from Attio API
  const { data: attioStatusOptions = [], isLoading: isLoadingStatusOptions } = useQuery({
    queryKey: [AttioQueryKeys.VALUATION_STATUS_OPTIONS],
    queryFn: getValuationStatusOptions,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });

  // Transform AttioStatusOption to StatusOption
  const statusOptions = useMemo(() => attioStatusOptions.map(option => ({
    id: option.id.option_id,
    label: option.title,
    isArchived: option.is_archived,
  })), [attioStatusOptions]);

  // Update transformedDetail with statusId if we have status options and the status matches
  const transformedDetailWithStatusId = useMemo(() => {
    if (!transformedDetail) {
      return transformedDetail;
    }
    // Try to find statusId from status options if status is available
    if (transformedDetail.status && statusOptions.length > 0) {
      const matchingStatus = statusOptions.find(option => option.label === transformedDetail.status);
      if (matchingStatus) {
        return {
          ...transformedDetail,
          statusId: matchingStatus.id,
        };
      }
    }
    return transformedDetail;
  }, [transformedDetail, statusOptions]);

  // Always make API call to get full detail, but show transformed data immediately
  // Skip API call only if we already have full detail structure
  const shouldSkipApiCall = useMemo(() => hasFullDetail, [hasFullDetail]);

  const {
    data: valuationDetailFromQuery,
    isError,
    error,
    isLoading,
    refetch: refetchValuationDetail,
  } = useQuery<AttioValuationDetailResponse, Error>({
    queryKey: [AttioQueryKeys.VALUATION_ITEM, valuationId],
    queryFn: () => getAttioValuationDetail(valuationId as string),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: !!valuationId && open && !shouldSkipApiCall, // Make API call unless we already have full detail
  });

  // Use transformed data immediately, then merge with API response when it arrives
  // This provides progressive/lazy loading: show what we have, enhance with API data
  const valuationDetail = useMemo(() => {
    // If we already have full detail, use it
    if (hasFullDetail && isFullDetailResponse(data)) {
      return data;
    }

    // If we have API response, use it (it's the most complete)
    if (valuationDetailFromQuery) {
      return valuationDetailFromQuery;
    }

    // Otherwise, use transformed data from table row (show immediately while API loads)
    if (transformedDetailWithStatusId) {
      return transformedDetailWithStatusId;
    }

    return undefined;
  }, [hasFullDetail, data, transformedDetailWithStatusId, valuationDetailFromQuery]);

  return {
    valuationDetail,
    valuationId,
    isLoading,
    isError,
    error,
    refetchValuationDetail,
    statusOptions,
    isLoadingStatusOptions,
  };
};

export const useOperationsModal = ({ models }: { models: ModelWithValuationData[] }) => {
  const [openModal, setOpenModal] = useState(false);
  const [selectedModelId, setSelectedModelId] = useState<number | undefined>();

  // Derive the selected model from models reactively
  const selectedModel = useMemo(() => {
    if (!selectedModelId) {
      return undefined;
    }
    return models.find(
      model => model.id === selectedModelId || model.company_measurement_date === selectedModelId
    );
  }, [models, selectedModelId]);

  const handleOpenModal = useCallback((model: ModelWithValuationData) => {
    // Store the ID or company_measurement_date as identifier
    setSelectedModelId(model.id || model.company_measurement_date);
    setOpenModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setOpenModal(false);
    setSelectedModelId(undefined);
  }, []);

  return {
    openModal,
    selectedModel,
    handleOpenModal,
    handleCloseModal,
  };
};

